import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import {
  BaseChartConfig,
  DashboardElement,
  isChartDashboardElement,
  isMapDashboardElement,
  isTableDashboardElement,
  TableConfig,
} from 'shared/models/chart.models';
import { TableActions, TableActionTypes } from '../table/table.actions';
import { ChartActions, ChartActionTypes } from '../chart/chart.actions';
import { PageActions, PageActionTypes } from '../page/page.actions';
import { DashboardElementUtil } from 'core/utils/dashboard-element-util';
import { ChartDataResponse } from 'core/services/dataRequest';
import { AppConfigActions, AppConfigActionTypes } from '../app/appConfig.actions';
import { WATCHLIST_PAGE } from '../../../../core/mappers/config-mapper/app-mapper.constants';

export const chartsAdapter = createEntityAdapter<DashboardElement>({
  selectId: chart => chart.id,
});

export interface ChartsState extends EntityState<DashboardElement> {
  processing: boolean;
  loading: boolean;
  loaded: boolean;
  loadError: boolean;
}

export const initialState: ChartsState = chartsAdapter.getInitialState({
  loading: false,
  loaded: false,
  processing: false,
  loadError: false,
});

export interface ChartEntities {
  [id: string]: DashboardElement;
}

export function ChartsReducer(
  state: ChartsState = initialState,
  action: ChartActions | PageActions | TableActions | AppConfigActions
): ChartsState {
  if (!window.reducerTimingDisabled) window.addTiming && window.addTiming('chart.reducer:' + action.type);

  switch (action.type) {
    case PageActionTypes.UPDATE_CHART_FILTER:
    case PageActionTypes.FILTER_BAR_EXPRESSION_UPDATE:
    case PageActionTypes.FETCH_PAGE_DATA_ON_DATE_CHANGE:
    case PageActionTypes.FETCH_PAGE_DATA_ON_SHIFT_CHANGE:
    case PageActionTypes.FETCH_PAGE_DATA_ON_PAGE_CHANGE:
    case TableActionTypes.UPDATE_PAGE_NUMBER:
    case TableActionTypes.UPDATE_PAGE_SIZE:
    case TableActionTypes.UPDATE_TABLE_COLUMN_SORT:
    case TableActionTypes.FETCH_TABLE_EXPORT_DATA:
    case AppConfigActionTypes.CHANGE_APP_CONFIG:
    case ChartActionTypes.UPDATE_PARAMETERS:
    case PageActionTypes.RESET_PAGE_FILTERS:
    case PageActionTypes.FETCH_PAGE_DATA_ON_REPORT_SELECTED:
    case ChartActionTypes.UPDATE_MAP_TYPE:
    case PageActionTypes.FETCH_CHILD_PAGE_DATA:
      return { ...state, loading: true, loadError: false };

    case ChartActionTypes.START_CHART_PROCESSING:
      return { ...state, processing: true };

    case ChartActionTypes.STOP_CHART_PROCESSING:
      return { ...state, processing: false };

    case TableActionTypes.FETCH_TABLE_EXPORT_DATA_SUCCESS:
    case PageActionTypes.NO_DATASET_FOUND:
      return { ...state, loading: false, loadError: false };

    case ChartActionTypes.CLEAR_CHART_FILTER_SELECTED_ATTRIBUTE: {
      const chart = state.entities[action.chartId];
      if (chart) {
        const chartNoFilterSelected = { ...chart, filterSelected: null };
        return chartsAdapter.updateOne({ id: chart.id, changes: chartNoFilterSelected }, state);
      }
      return state;
    }

    case ChartActionTypes.UPDATE_CHART: {
      if (action.chartUpdate) {
        return chartsAdapter.updateOne(action.chartUpdate, state);
      }
      return state;
    }

    case ChartActionTypes.UPDATE_CHARTS: {
      if (action.chartsUpdate) {
        return chartsAdapter.updateMany(action.chartsUpdate, state);
      }
      return state;
    }

    case ChartActionTypes.UPDATE_CHART_FILTER: {
      if (action.charts) {
        return chartsAdapter.updateMany(action.charts, { ...state, loading: false, loaded: true, loadError: false });
      }
      return { ...state, loading: true };
    }

    case PageActionTypes.UPDATE_CHART_LEGENDS: {
      const chartWithLegends = { ...action.filter.chart, legends: action.filter.legends };
      return chartsAdapter.updateOne(
        { id: action.filter.chartId, changes: chartWithLegends },
        { ...state, loading: false, loaded: true, loadError: false }
      );
    }

    case ChartActionTypes.UPDATE_ATTRIBUTES_SUCCESS: {
      return chartsAdapter.updateOne(action.updateConfigWithAttributes[0], {
        ...state,
        loading: false,
        loaded: true,
        loadError: false,
      });
    }
    case ChartActionTypes.ADD_CHART: {
      return chartsAdapter.addOne(action.chart, state);
    }
    case ChartActionTypes.ADD_CHARTS_SUCCESS: {
      return chartsAdapter.addMany(action.charts, state);
    }
    case ChartActionTypes.DELETE_PAGE_CHARTS: {
      const pageId = action.pageId;
      const chartsToDelete: string[] = Object.keys(state.entities).filter(id => {
        const chart: DashboardElement = state.entities[id];
        return chart && chart.pageId === pageId;
      });
      return chartsAdapter.removeMany(chartsToDelete, state);
    }
    case ChartActionTypes.DELETE_CHART: {
      return chartsAdapter.removeOne(action.id, state);
    }
    case ChartActionTypes.GET_CHARTS_SUCCESS: {
      return chartsAdapter.setAll(action.charts, state);
    }

    case ChartActionTypes.GET_CHART_TABLE_DATA_SUCCESS: {
      return chartsAdapter.updateOne(action.chartTable, { ...state, loading: false, loaded: true, loadError: false });
    }

    case ChartActionTypes.UPDATE_SPECIFIC_MAP_FILTER: {
      return { ...state, loading: true, loaded: false, loadError: false };
    }

    case ChartActionTypes.UPDATE_MAP_FILTER_SUCCESS: {
      const update: Update<DashboardElement>[] = action.dataResponses.map(dr => {
        const chart = state.entities[dr.chartId];
        return {
          id: dr.chartId,
          changes: {
            filters: {
              ...chart.filters,
              filterBounds: action.mapFilter.filterBounds,
            },
            record: {
              data: dr.data,
              recordCount: dr.recordCount,
              noData: dr.noData,
              deviceId: action.deviceId,
              metadata: dr.metadata,
            },
          },
        };
      });

      let mapUpdate = update.find(u => u.id === action.sourceMapId);
      if (mapUpdate) {
        //   mapUpdate = {
        //     id: action.sourceMapId,
        //     changes: {
        //       // filterSelected: true,
        //       // filters: action.mapFilter,
        //     },
        //   };
        //   update.push(mapUpdate);
        // } else {
        mapUpdate.changes = {
          ...mapUpdate.changes,
          filterSelected: true,
          filters: action.mapFilter,
        };
      }

      return chartsAdapter.updateMany(update, { ...state, loading: false, loaded: true, loadError: false });
    }

    case ChartActionTypes.UPDATE_MAP_TYPE_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartResponse.map(data => {
        const chart = state.entities[data.chartId];
        return {
          id: data.chartId,
          changes: {
            record: {
              data: data.data,
              recordCount: data.recordCount,
              noData: data.noData,
            },
          },
        };
      });
      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case TableActionTypes.UPDATE_TABLE_FILTER_SUCCESS: {
      const chart = state.entities[action.chartId];
      if (isTableDashboardElement(chart)) {
        const tableChange: Partial<TableConfig> = {
          ...chart,
          filters: action.tableFilter,
          record: {
            data: action.record.data,
            recordCount: action.record.recordCount,
          },
        };
        return chartsAdapter.updateOne(
          { id: chart.id, changes: tableChange },
          { ...state, loading: false, loaded: true, loadError: false }
        );
      }
      return state;
    }

    case ChartActionTypes.ADD_UI_MAP_PARAMETERS: {
      const chart = state.entities[action.chartId];
      if (isMapDashboardElement(chart)) {
        const mapUiParams = {
          ...chart,
          uiParams: action.mapUIParameters,
        };
        return chartsAdapter.updateOne({ id: chart.id, changes: mapUiParams }, state);
      }
      return state;
    }

    case ChartActionTypes.UPDATE_MAP_BOUNDS_AND_ZOOM_NO_DATA_FETCH: {
      const chart = state.entities[action.chartId];
      if (isMapDashboardElement(chart)) {
        const filterChange = {
          ...chart,
          filters: {
            ...chart.filters,
            mapBounds: action.mapBounds,
            mapZoom: action.mapZoom,
          },
        };
        return chartsAdapter.updateOne({ id: chart.id, changes: filterChange }, state);
      }
      return state;
    }

    case ChartActionTypes.REVERT_CHART_STATE: {
      const chartsDataUpdate: Update<BaseChartConfig>[] = action.charts.map(ch => {
        const update: Update<BaseChartConfig> = {
          id: ch.id,
          changes: {
            ...ch,
            filters: ch.filters,
            uiParams: ch.uiParams,
            filterSelected: ch.filterSelected,
            selections: ch.selections,
            record: {
              data: null,
              recordCount: null,
            },
          },
        };
        return update;
      });
      return chartsAdapter.updateMany(chartsDataUpdate, state);
    }

    case ChartActionTypes.HYDRATE_CHARTS_SUCCESS: {
      const chartsUpdate = DashboardElementUtil.updateCascadedChartFilterSelections(
        Object.values(state.entities).filter(c => action.chartIds.indexOf(c.id) >= 0) as BaseChartConfig[],
        action.cascadingFilters
      );

      return chartsAdapter.updateMany(chartsUpdate, state);
    }

    case ChartActionTypes.HYDRATE_CHART: {
      const update: Update<DashboardElement>[] = Object.keys(action.json).map(chartId => {
        return {
          id: chartId,
          changes: {
            filters: action.json[chartId].filters,
          },
        };
      });
      return chartsAdapter.updateMany(update, state);
    }

    case PageActionTypes.UPDATE_CHART_FILTER_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        let chart = state.entities[data.chartId] as BaseChartConfig;
        const update = storeChartDataRecords(state, data, true);

        if (chart.id === action.filter.chartId) {
          if (action.filter.filterAdded) {
            chart = DashboardElementUtil.addChartFilterSelections([chart], action.filter)[0];
          } else {
            chart = DashboardElementUtil.removeChartFilterSelections([chart], action.filter.chartId)[0];
          }
          update.changes = {
            ...update.changes,
            selections: chart.selections,
            filterSelected: chart.filterSelected,
            filters: chart.filters,
          };
        }

        return update;
      });

      // Patch in filter changes that might not be included in response
      if (!action.chartsData.find(chartData => chartData.chartId === action.filter.chartId)) {
        const update: Update<BaseChartConfig> = { id: action.filter.chartId, changes: {} };
        let chart = state.entities[action.filter.chartId] as BaseChartConfig;
        if (action.filter.filterAdded) {
          chart = DashboardElementUtil.addChartFilterSelections([chart], action.filter)[0];
        } else {
          chart = DashboardElementUtil.removeChartFilterSelections([chart], action.filter.chartId)[0];
        }
        update.changes = {
          ...update.changes,
          selections: chart.selections,
          filterSelected: chart.filterSelected,
          filters: chart.filters,
        };
        chartsDataUpdate.push(update);
      }

      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }
    case PageActionTypes.FETCH_CHILD_PAGE_DATA_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const chart = state.entities[data.chartId];
        const update = storeChartDataRecords(state, { ...data, deviceId: action.deviceId }, true);

        // reset chart selection where needed
        if (action.resetSelectionAndFilter) {
          if (isChartDashboardElement(chart)) {
            update.changes.selections = [];
            update.changes.filterSelected = false;
          } else if (isMapDashboardElement(chart)) {
            update.changes.filters = DashboardElementUtil.createDefaultMapFilters();
            update.changes.filterSelected = false;
          }
        }

        return update;
      });
      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }
    case PageActionTypes.FETCH_CHILD_PAGE_DATA_ON_REPORT_SELECTED_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(state, { ...data, deviceId: action.deviceId }, true);
        return update;
      });
      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case PageActionTypes.SELECT_MODAL_PAGE_WITH_DATE_SUCCESS:
    case PageActionTypes.UPDATE_STORE_FILTER_SUCCESS:
    case PageActionTypes.UPDATE_GROUP_FILTER_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const chart = state.entities[data.chartId];
        const update = storeChartDataRecords(state, data, true);

        // reset chart selection where needed
        if (action.resetSelectionAndFilter) {
          if (isChartDashboardElement(chart)) {
            update.changes.selections = [];
            update.changes.filterSelected = false;
          } else if (isMapDashboardElement(chart)) {
            update.changes.filters = DashboardElementUtil.createDefaultMapFilters();
            update.changes.filterSelected = false;
          }
        }

        return update;
      });
      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case PageActionTypes.CLEAR_ALL_CASCADING_FILTERS_SUCCESS: {
      const update: Update<BaseChartConfig>[] = Object.keys(state.entities).map(chartId => {
        return {
          id: chartId,
          changes: {
            filterSelected: false,
            selections: [],
          },
        };
      });

      return chartsAdapter.updateMany(update, state);
    }

    case PageActionTypes.CLEAR_PAGE_CASCADING_FILTERS_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(state, data, true);
        update.changes = { ...update.changes, selections: [], filterSelected: false };
        return update;
      });
      return chartsAdapter.updateMany(chartsDataUpdate, state);
    }

    case PageActionTypes.RESET_PAGE_FILTERS_SUCCESS: {
      if (action.pageId.startsWith(WATCHLIST_PAGE)) {
        return { ...state, loading: false, loaded: true, loadError: false };
      }
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(state, data, true);
        update.changes = {
          ...update.changes,
          selections: [],
          filterSelected: false,
        };
        return update;
      });
      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case PageActionTypes.FETCH_PAGE_DATA_ON_SHIFT_CHANGE_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(
          state,
          data,
          action.type === PageActionTypes.FETCH_PAGE_DATA_ON_SHIFT_CHANGE_SUCCESS
        );
        if (action.resetSelectionAndFilter) {
          update.changes.selections = [];
          update.changes.filterSelected = false;
          if (state.entities[data.chartId] && state.entities[data.chartId].elementType === 'map') {
            update.changes.filters = {
              ...state.entities[data.chartId].filters,
              selectedMarkerIds: undefined,
              selectedMarkers: [],
              lastSelectedMarkerId: null,
              legends: null,
              mapZoom: null,
              mapBounds: null,
            };
          }
        }
        return update;
      });
      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case PageActionTypes.FETCH_PAGE_DATA_ON_DATE_CHANGE_SUCCESS:
    case PageActionTypes.FETCH_PAGE_DATA_ON_PAGE_CHANGED_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(
          state,
          data,
          action.type === PageActionTypes.FETCH_PAGE_DATA_ON_DATE_CHANGE_SUCCESS
        );

        // reset selections if needed
        if (action.resetSelectionAndFilter) {
          update.changes.selections = [];
          update.changes.filterSelected = false;
          if (state.entities[data.chartId] && state.entities[data.chartId].elementType === 'map') {
            update.changes.filters = {
              ...state.entities[data.chartId].filters,
              selectedMarkerIds: undefined,
              selectedMarkers: [],
              lastSelectedMarkerId: null,
              legends: null,
              mapZoom: null,
              mapBounds: null,
            };
          }
        }
        return update;
      });

      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case PageActionTypes.FETCH_PAGE_DATA_ON_REPORT_SELECTED_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(
          state,
          data,
          action.type === PageActionTypes.FETCH_PAGE_DATA_ON_REPORT_SELECTED_SUCCESS
        );
        return update;
      });

      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case PageActionTypes.FETCH_PAGE_DATA_ON_DATE_CHANGE_FAILURE:
    case PageActionTypes.FETCH_PAGE_DATA_ON_SHIFT_CHANGE_FAILURE:
    case PageActionTypes.FETCH_PAGE_DATA_ON_PAGE_CHANGED_FAILURE:
    case TableActionTypes.UPDATE_TABLE_FILTER_FAILURE: {
      console.error(action.error);
      return { ...state, loading: false, loaded: false, loadError: true };
    }
    case ChartActionTypes.UPDATE_PARAMETERS_SUCCESS: {
      const update = storeChartDataRecords(state, action.chartsData, true);

      update.changes.filters = { ...update.changes.filters, ...action.parameters };

      return { ...chartsAdapter.updateOne(update, state), loading: false, loaded: true, loadError: false };
    }
    case ChartActionTypes.UPDATE_BULK_CHART_PARAMETERS_SUCCESS: {
      const chartsDataUpdate: Update<BaseChartConfig>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(state, data, false);
        update.changes.filters = {
          ...update.changes.filters,
          ...action.charts.find(chart => chart.id === data.chartId)?.filters,
        };
        update.changes.uiParams = {
          ...update.changes.uiParams,
          ...action.charts.find(chart => chart.id === data.chartId)?.uiParams,
        };

        return update;
      });
      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }

    case ChartActionTypes.UPDATE_BULK_CHART_SELECTIONS_SUCCESS: {
      const chartsSelectedUpdate = DashboardElementUtil.updateCascadedChartFilterSelections(
        Object.values(state.entities).filter(c => action.chartIds.indexOf(c.id) >= 0) as BaseChartConfig[],
        action.cascadingFilters
      );
      const chartsUpdate: Update<BaseChartConfig>[] = [];
      action.chartIds.forEach(chartId => {
        const selectedChart = chartsSelectedUpdate.find(update => update.id === chartId);
        let updateSelect;
        if (selectedChart) {
          updateSelect = {
            id: chartId,
            changes: selectedChart.changes,
          };
        } else {
          updateSelect = {
            id: chartId,
            changes: {
              filterSelected: false,
              selections: [],
            },
          };
        }
        chartsUpdate.push(updateSelect);
      });

      return chartsAdapter.updateMany(chartsUpdate, state);
    }

    case PageActionTypes.RELOAD_PAGE_SUCCESS: {
      const chartsDataUpdate: Update<BaseChartConfig>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(state, data, false);

        update.changes = { ...update.changes, selections: [], filterSelected: false };

        return update;
      });

      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }
    case PageActionTypes.CLEAR_PAGE_CHART_FILTERS_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(state, data, true);

        update.changes = { ...update.changes, selections: [], filterSelected: false };

        return update;
      });

      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }
    case PageActionTypes.UPDATE_PREDEFINED_CHART_CONFIG_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data =>
        storeChartDataRecords(state, data, true)
      );

      const hiddenChartsFilterUpdate: Update<DashboardElement>[] = action.hiddenChartIds.map(chartId => {
        const update: Update<BaseChartConfig> = {
          id: chartId,
          changes: { selections: [], filterSelected: false },
        };
        return update;
      });

      return {
        ...chartsAdapter.updateMany([...chartsDataUpdate, ...hiddenChartsFilterUpdate], state),
        loading: false,
        loaded: true,
        loadError: false,
      };
    }

    case PageActionTypes.FILTER_BAR_EXPRESSION_UPDATE_SUCCESS: {
      const chartsDataUpdate: Update<DashboardElement>[] = action.chartsData.map(data => {
        const update = storeChartDataRecords(state, data, true);

        update.changes.selections = [];
        update.changes.filterSelected = false;
        if (state.entities[data.chartId] && state.entities[data.chartId].elementType === 'map') {
          update.changes.filters = {
            ...state.entities[data.chartId].filters,
            selectedMarkerIds: undefined,
            selectedMarkers: [],
            lastSelectedMarkerId: null,
            legends: null,
            mapZoom: null,
            mapBounds: null,
          };
        }
        return update;
      });

      return { ...chartsAdapter.updateMany(chartsDataUpdate, state), loading: false, loaded: true, loadError: false };
    }
    case ChartActionTypes.ADD_ZOOM_RANGE: {
      const chart = state.entities[action.chartId];
      if (isChartDashboardElement(chart)) {
        const chartZoomRange = {
          ...chart,
          zoomRange: action.zoomRange,
        };
        return chartsAdapter.updateOne({ id: chart.id, changes: chartZoomRange }, state);
      }
      return state;
    }

    case PageActionTypes.UPDATE_MULTI_CHART_SELECTION_SUCCESS: {
      const chartsUpdate: Update<BaseChartConfig>[] = action.charts.map(data => {
        const update: Update<BaseChartConfig> = {
          id: data.id,
          changes: {
            ...data,
          },
        };
        return update;
      });

      return { ...chartsAdapter.updateMany(chartsUpdate, state), loading: false, loaded: true, loadError: false };
    }

    default: {
      return state;
    }
  }
}

function storeChartDataRecords(
  state: ChartsState,
  data: ChartDataResponse,
  resetTablePages: boolean
): Update<BaseChartConfig> {
  const chart = state.entities[data.chartId];
  const update: Update<BaseChartConfig> = {
    id: data.chartId,
    changes: {
      record: {
        data: data.data,
        recordCount: data.recordCount,
        noData: data.noData,
        deviceId: data.deviceId,
        metadata: data.metadata,
      },
    },
  };

  // reset page number for every table
  if (resetTablePages && isTableDashboardElement(chart)) {
    update.changes.filters = { ...chart.filters, pageSkip: 0 };
    delete update.changes.filters.filterBounds;
  }

  return update;
}
